import React, { useEffect, useRef, useState } from "react";

import {
  TFunction,
  getLocalStorageCountry,
  getLocalStorageZipCode,
  setLocalStorageCountry,
  setLocalStorageHectare,
  setLocalStorageZipCode,
  useAddressValidation,
  useTranslation,
  useUserContext,
} from "utils";

import { COUNTRY_CODE } from "consts";

import { User } from "models";

import { BasicModal, CarouselDotted, Flex, Form, Icon, Input, InputNumber, Option, Select, Spin, Svg } from "components";

type FormType = {
  zipCode: string;
  hectare: number;
  country: string;
};

type OnboardingModalProps = {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  onFinish: ({ zipCode, country, hectares }: { zipCode: string; country: string; hectares: number }) => Promise<void>;
  isOnboarded: boolean;
  isPending?: boolean;
  afterClose?: () => void;
};

const defaultFieldSize = 10;
const availableCountries = [COUNTRY_CODE.GERMANY, COUNTRY_CODE.POLAND];

export const OnboardingModal = ({ isOpen, setIsOpen, onFinish, isOnboarded, isPending, afterClose }: OnboardingModalProps) => {
  const { t } = useTranslation(["cropExplorer", "onboarding", "enums"]);
  const { user } = useUserContext();
  const [form] = Form.useForm();

  const [country, setCountry] = useState<string>(getLocalStorageCountry());
  const zipCode = getLocalStorageZipCode();
  const [isLoading, setIsLoading] = useState<boolean>();
  const countries = t("enums:countries", { returnObjects: true });
  const delayedTimeout = useRef<NodeJS.Timeout | null>(null);

  const handleFinish = async () => {
    form
      .validateFields()
      .then((formValues: FormType) => {
        setIsLoading(true);

        if (!user) {
          setLocalStorageHectare(formValues.hectare ?? defaultFieldSize);
          setLocalStorageCountry((formValues.country as string) ?? country);
          setLocalStorageZipCode((formValues.zipCode as string) ?? zipCode);
        }

        delayedTimeout.current = setTimeout(
          async () => {
            await onFinish({
              hectares: formValues.hectare ?? defaultFieldSize,
              country: (formValues.country as string) ?? country,
              zipCode: (formValues.zipCode as string) ?? zipCode,
            });
          },
          isOnboarded ? 500 : 3000
        );
      })
      .catch(() => {});
  };

  // eslint-disable-next-line arrow-body-style
  useEffect(() => {
    return () => {
      clearTimeout(delayedTimeout.current as NodeJS.Timeout);
      delayedTimeout.current = null;

      setIsLoading(false);
      setIsOpen(false);
    };
  }, [setIsOpen]);

  useEffect(() => {
    if (delayedTimeout.current && !isPending) {
      setIsLoading(false);
      setIsOpen(false);
    }
  }, [isPending, setIsOpen]);

  const handleCountryChange = (value: string) => {
    setCountry(value);
    form.validateFields(["zipCode"], { dirty: true });
  };

  const makeNavigationBarActive = (user: User | null, active: boolean) => {
    // Change the z-index of the side or top navigation to make it accessible (the z-index of the modal is 1000)
    if (user) {
      const sideNavigation = document.getElementsByTagName("aside")[0];
      // We need the "Optional Chaining" here because of the unit tests since the "aside" is not part of this component.
      if (sideNavigation?.style) {
        sideNavigation.style.zIndex = active ? "1001" : "100";
      }
      return;
    }

    const topNavigation = document.getElementsByClassName("nav-container")[0] as HTMLElement;
    if (topNavigation?.style) {
      topNavigation.style.zIndex = active ? "1001" : "100";
      topNavigation.style.backgroundColor = "white";
    }
  };

  useEffect(() => {
    makeNavigationBarActive(user, isOpen);

    return () => makeNavigationBarActive(user, false);
  }, [user, isOpen]);

  return (
    <BasicModal
      className="crop-explorer-onboarding-modal"
      data-testid="crop-explorer-onboarding-modal"
      afterClose={afterClose}
      closable={false}
      open={isOpen}
      footer={null}
      centered
    >
      <Flex vertical={false} justify="space-between" gap="1rem">
        <Flex className="title-card" vertical align="center" justify="space-between">
          <h1>{t("cropExplorer:onboardingModal.title")}</h1>
          <Svg svgName="icons/klim/graph" height={96} width={96} />
        </Flex>
        <Flex className="content">
          {isLoading ? (
            <LoadingScreen t={t} />
          ) : (
            <CarouselDotted
              nextButtonDataTestId="crop-explorer-onboarding-modal-next-button"
              backButtonDataTestId="crop-explorer-onboarding-modal-back-button"
              onFinish={handleFinish}
            >
              {!isOnboarded && <WelcomeScreen t={t} />}
              <Form
                data-testid="crop-explorer-onboarding-modal-data-input-screen"
                className={`crop-explorer-onboarding-modal form ${user && "form-center"}`}
                name="onboarding"
                form={form}
              >
                <DataInputScreen
                  t={t}
                  area={defaultFieldSize}
                  country={country}
                  countries={countries}
                  zipCode={zipCode === undefined ? form.getFieldValue("zipCode") : zipCode}
                  handleCountryChange={handleCountryChange}
                  user={user}
                />
              </Form>
            </CarouselDotted>
          )}
        </Flex>
      </Flex>
    </BasicModal>
  );
};

const WelcomeScreen = ({ t }: { t: TFunction }) => (
  <Flex data-testid="crop-explorer-onboarding-modal-welcome-screen" vertical={true} align="center" justify="center">
    <Icon iconName="klim/planting-plant" size="xl" />
    <h3 className="margin-top-100">{t("onboardingModal.welcomeHeader")}</h3>
    <p className="paragraph-m center">{t("onboardingModal.welcomeContent")}</p>
  </Flex>
);

const DataInputScreen = ({
  t,
  user,
  country,
  countries,
  zipCode,
  area,
  handleCountryChange,
}: {
  t: TFunction;
  user: User | null;
  country: string;
  countries: object;
  zipCode: string;
  area: number;
  handleCountryChange: (country: string) => void;
}) => {
  const { validateZipCode } = useAddressValidation({ country, t });

  const showHeader = !user || !zipCode || !country;
  const hasFarm = user?.farm?.name; // this is how we know if a user "has" a farm or not, for historical reasons
  const form = Form.useFormInstance();

  // We need this or the Input component loses the state of the value even though the form has it
  // Todo: let's create a ticket to fix it
  const zipCodeFormValue = zipCode ?? form.getFieldValue("zipCode");
  return (
    <>
      {showHeader && (
        <>
          <h4>{t("cropExplorer:onboardingModal.locationFormHeader")}</h4>
          <p className="paragraph-s">{t("cropExplorer:onboardingModal.locationFormDescription")}</p>
        </>
      )}
      {!user && (
        // @ts-expect-error convert this to ts
        <Select
          dataTestId="crop-explorer-onboarding-modal-data-input-country"
          className="margin-top-100"
          name="country"
          label={t("cropExplorer:onboardingModal.locationFormCountryInputPlaceholder")}
          rules={[{ required: true }]}
          value={country}
          initialValue={availableCountries.includes(country as COUNTRY_CODE) ? country : null}
          onChange={handleCountryChange}
          optionFilterProp="children"
          showSearch
        >
          {Object.entries(countries)
            .filter(([code]) => availableCountries.includes(code as COUNTRY_CODE))
            .map(([code, name]) => (
              <Option data-testid={`country-option-${code}`} key={code} value={code}>
                {name}
              </Option>
            ))}
        </Select>
      )}
      {!hasFarm && (
        <Input
          dataTestId="crop-explorer-onboarding-modal-data-input-zipcode"
          className="margin-top-100"
          name="zipCode"
          label={t("cropExplorer:onboardingModal.locationFormZipCodeInputPlaceholder")}
          validateTrigger="onBlur"
          initialValue={zipCodeFormValue}
          // The empty message is on purpose. We are already handling the empty input in the missingAddressValidator.
          rules={[{ required: true, message: "" }, { validator: (_, names) => validateZipCode(names) }]}
          suffix={<Icon iconName="tabler/search" />}
        />
      )}
      <h4 className="margin-top-200">{t("cropExplorer:onboardingModal.fieldFormHeader")}</h4>
      <p className="paragraph-s">{t("cropExplorer:onboardingModal.fieldFormDescription")}</p>
      <InputNumber
        dataTestId="crop-explorer-onboarding-modal-data-input-area"
        className="margin-top-100"
        name="hectare"
        label={t("cropExplorer:onboardingModal.fieldFormHectareInputPlaceholder")}
        suffix={t("cropExplorer:onboardingModal.fieldFormHectareInputSuffix")}
        initialValue={area}
        min={1}
        max={10000}
        value={area}
      />
    </>
  );
};

const LoadingScreen = ({ t }: { t: TFunction }) => (
  <Flex className="loading-step" vertical justify="center" align="center">
    <h4 className="margin-bottom-100">{t("cropExplorer:onboardingModal.loadingMessage")}</h4>
    <Spin />
  </Flex>
);
